import { createClient } from './supabase/client'
import { isNetworkError } from './offline'

/**
 * 111 — the app says when it breaks. Three kinds reach `client_errors`:
 *
 *   render     a screen crashed (components/error-boundary.tsx)
 *   unhandled  an exception or rejected promise nothing caught (src/main.tsx)
 *   rpc        the server refused and the app had no words for it (store's
 *              `failed()` — a refusal it KNOWS, like a closed shift, is not a bug)
 *
 * The morning digest counts them per shop. Before this, a crash in a shop
 * reached us as the owner's screenshot.
 *
 * Quiet by construction: never throws, never awaits in the caller's path,
 * skips the network being down (that is the offline queue's business, not a
 * bug), skips browser noise, and sends each distinct message once per page
 * load, at most 20 per load. The database caps it again (30/hour/user).
 * Only a signed-in session can write; before login the call is refused and
 * nothing happens.
 */

export type ErrorKind = 'render' | 'unhandled' | 'rpc'

const MAX_PER_LOAD = 20
const seen = new Set<string>()
let sent = 0

/** Browser and extension noise that says nothing about our code. */
const NOISE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Non-Error promise rejection captured/i,
  /AbortError|The operation was aborted/i,
]

function messageOf(err: unknown): string {
  if (err instanceof Error) return err.message
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message)
  return String(err ?? '')
}

/** The build a report came from: the hashed entry chunk names it exactly. */
function buildId(): string | null {
  if (typeof document === 'undefined') return null
  const src = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src')
  return src ? src.split('/').pop() ?? null : null
}

export function reportError(kind: ErrorKind, err: unknown, detail?: string | null): void {
  try {
    if (typeof window === 'undefined' || sent >= MAX_PER_LOAD) return
    const message = messageOf(err).trim().slice(0, 500)
    if (!message || NOISE.some((re) => re.test(message))) return
    if (isNetworkError(err) || !navigator.onLine) return
    const key = `${kind}:${message}`
    if (seen.has(key)) return
    seen.add(key)
    sent++
    const stack = detail ?? (err instanceof Error ? err.stack : (err as { code?: string } | null)?.code) ?? null
    void createClient().rpc('log_client_error', {
      p_kind: kind,
      p_message: message,
      p_detail: stack ? String(stack).slice(0, 2000) : null,
      p_route: window.location.pathname.slice(0, 200),
      p_build: buildId(),
      p_user_agent: navigator.userAgent.slice(0, 300),
    }).then(() => {}, () => {})
  } catch {
    // Reporting must never be the thing that breaks the till.
  }
}

/** Catch what nothing else caught. Called once from src/main.tsx. */
export function listenForUncaughtErrors(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('error', (e) => {
    // A failed <script>/<img> load arrives here without an error object.
    if (!e.error && !e.message) return
    reportError('unhandled', e.error ?? e.message, e.error?.stack ?? `${e.filename ?? ''}:${e.lineno ?? ''}`)
  })
  window.addEventListener('unhandledrejection', (e) => {
    reportError('unhandled', e.reason)
  })
}
